import { centsToDisplay } from '../../lib/money';
import type { Currency } from '../../types/account';

// Pure helpers for the click-to-detail "Budget modified" popover on the
// Daily Budget cell (P2b post-Chunk-C polish). Split out so the formatting
// + open/close state machine can be unit-tested without rendering.
//
// The component (DailyBudgetCell.tsx) owns layout + DOM; this file owns:
//   - buildBudgetModifiedContent — turns row + Account inputs into the
//     three strings the popover renders.
//   - reducePopoverOpen — the toggle behaviour for the discriminated
//     "which popover is open" state. The cell hosts two popovers (the
//     existing daily-breakdown + this new one) and they're mutually
//     exclusive: clicking one while the other is open just swaps; clicking
//     the same trigger twice closes.

export type PopoverPanel = 'breakdown' | 'modified';
export type PopoverOpen = PopoverPanel | null;

// Toggle-or-swap. Clicking the open panel's trigger closes it; clicking
// the other trigger swaps. Distinct close action for click-outside /
// Escape / explicit dismiss button.
export function reducePopoverOpen(
  prev: PopoverOpen,
  action: PopoverPanel | 'close',
): PopoverOpen {
  if (action === 'close') return null;
  return prev === action ? null : action;
}

export type BudgetModifiedContent = {
  title: string;             // e.g. "Meta Budget — modified"
  accountLine: string;       // e.g. "$500.00 / month" or "—"
  rowLine: string;           // e.g. "$600.00 / month"
  increaseLine: string;      // e.g. "$0.00" — never suppressed; zero is meaningful
};

export type BudgetModifiedInput = {
  platform: 'meta' | 'google';
  currency: Currency;
  accountMonthlyBudget: number | null | undefined;  // cents — Account.{platform}MasterMonthlyBudget
  rowMonthlyBudget: number | null;                   // cents — row.{platform}MonthlyBudget
  rowIncrease: number;                               // cents — row.{platform}CampaignIncrease
};

// "$X.XX / month" or "—" for null/undefined Account defaults. Centralised
// so the dash semantics stay consistent — an unset Account budget reads as
// "no value to compare against", not "$0.00".
function formatMonthly(cents: number | null | undefined, currency: Currency): string {
  if (cents === null || cents === undefined) return '—';
  return `${centsToDisplay(cents, currency)} / month`;
}

export function buildBudgetModifiedContent(
  input: BudgetModifiedInput,
): BudgetModifiedContent {
  const platformLabel = input.platform === 'meta' ? 'Meta' : 'Google';
  return {
    title: `${platformLabel} Budget — modified`,
    accountLine: formatMonthly(input.accountMonthlyBudget, input.currency),
    rowLine: formatMonthly(input.rowMonthlyBudget, input.currency),
    // Increase reads as a flat amount (no "/ month") — it's a per-rollout
    // top-up, not a recurring charge. Zero is meaningful: it tells the AM
    // "the only modification is to Monthly", so we render "$0.00" rather
    // than suppress.
    increaseLine: centsToDisplay(input.rowIncrease, input.currency),
  };
}
